import React, { useState, useEffect, useCallback } from 'react';
import { X, Lock, Unlock, RefreshCw, AlertTriangle, ArrowRightLeft, HelpCircle } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { pickingService } from '../../../../services/pickingService';
import { PListLockPeriodDto, PListLockOperatorSummaryDto } from '../../../../types/picking';

interface PListLockHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Lagersjef-visning av lås/opplås-adferd på plukklister (ta-og-slipp-sporing,
 * Haucon Teamwork 36333980). Viser aggregert oversikt per operatør + én rad per
 * lås (TW 36396507) med tatt/sluppet/holdetid/status. Datakilde: PListLockHistory
 * (migrasjon 081), paret til perioder i backend (lock-history/periods).
 */
export const PListLockHistoryModal: React.FC<PListLockHistoryModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<PListLockOperatorSummaryDto[]>([]);
  const [periods, setPeriods] = useState<PListLockPeriodDto[]>([]);
  // Løpende holdetid for «Holdes nå»: backend gir holdetid frem til hentetidspunktet,
  // klienten legger på tiden som har gått siden.
  const [fetchedAt, setFetchedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operatorFilter, setOperatorFilter] = useState<string | null>(null);
  // Raden der alle ERP-ordrene vises (trykk på «+N» — touch, ikke hover)
  const [expandedErpKey, setExpandedErpKey] = useState<string | null>(null);

  const loadData = useCallback(async (periodDays: number, operatorId: string | null) => {
    try {
      setLoading(true);
      setError(null);
      const [summaryData, periodData] = await Promise.all([
        pickingService.getLockHistorySummary(periodDays),
        pickingService.getLockPeriods(periodDays, operatorId ?? undefined)
      ]);
      setSummary(summaryData);
      setPeriods(periodData);
      const loadedAt = Date.now();
      setFetchedAt(loadedAt);
      setNow(loadedAt);
    } catch (err) {
      console.error('Error loading lock history:', err);
      setError(t('manager.modules.newPickingLists.lockHistory.loadError', {}, 'Kunne ikke laste låsehistorikk.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      loadData(days, operatorFilter);
    }
  }, [isOpen, days, operatorFilter, loadData]);

  const hasHeldNow = periods.some(p => p.status === 'HeldNow');
  useEffect(() => {
    if (!isOpen || !hasHeldNow) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [isOpen, hasHeldNow]);

  if (!isOpen) return null;

  const lh = 'manager.modules.newPickingLists.lockHistory';

  const statusBadge = (period: PListLockPeriodDto) => {
    const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';
    switch (period.status) {
      case 'HeldNow':
        return <span className={`${base} bg-green-100 text-green-800`}><Lock size={11} /> {t(`${lh}.statusHeldNow`, {}, 'Holdes nå')}</span>;
      case 'Released':
        return <span className={`${base} bg-gray-100 text-gray-700`}><Unlock size={11} /> {t(`${lh}.statusReleased`, {}, 'Sluppet')}</span>;
      case 'ForceReleased':
        return <span className={`${base} bg-red-100 text-red-700`}><AlertTriangle size={11} /> {t(`${lh}.statusForceReleased`, {}, 'Tvang')}</span>;
      case 'Reassigned':
        return <span className={`${base} bg-blue-100 text-blue-700`}><ArrowRightLeft size={11} /> {t(`${lh}.statusReassigned`, {}, 'Omfordelt')}</span>;
      default:
        return <span className={`${base} bg-amber-100 text-amber-800`}><HelpCircle size={11} /> {t(`${lh}.statusEndedUnlogged`, {}, 'Avsluttet uten logg')}</span>;
    }
  };

  /** Hvem som tvangs-slapp / hvem låsen ble omfordelt til — vises under status-merket. */
  const statusDetail = (period: PListLockPeriodDto): string | null => {
    if (period.status === 'Reassigned' && period.reassignedTo) {
      const by = period.releasedBy ? `${t(`${lh}.byOperator`, { '0': period.releasedBy }, `av ${period.releasedBy}`)}, ` : '';
      return by + t(`${lh}.toOperator`, { '0': period.reassignedTo }, `til ${period.reassignedTo}`);
    }
    if (period.status === 'ForceReleased' && period.releasedBy) {
      return t(`${lh}.byOperator`, { '0': period.releasedBy }, `av ${period.releasedBy}`);
    }
    if (period.status === 'Released' && period.releasedBy && period.heldBy
        && period.releasedBy.toLowerCase() !== period.heldBy.toLowerCase()) {
      return t(`${lh}.byOperator`, { '0': period.releasedBy }, `av ${period.releasedBy}`);
    }
    return null;
  };

  const heldMinutesFor = (period: PListLockPeriodDto): number | undefined => {
    if (period.heldMinutes === undefined || period.heldMinutes === null) return undefined;
    if (period.status === 'HeldNow') {
      return period.heldMinutes + Math.max(0, now - fetchedAt) / 60000;
    }
    return period.heldMinutes;
  };

  const periodKey = (period: PListLockPeriodDto): string =>
    `${period.pListID}-${period.takenEventID ?? 'x'}-${period.releaseEventID ?? 'x'}`;

  const hasUnlogged = periods.some(p => p.status === 'EndedUnlogged' || !p.takenAt);

  const formatHeldMinutes = (minutes?: number | null): string => {
    if (minutes === undefined || minutes === null) return '-';
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}t ${Math.floor(minutes % 60)}m`;
    return `${Math.floor(minutes / (60 * 24))}d ${Math.floor((minutes % (60 * 24)) / 60)}t`;
  };

  const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Lock size={18} className="text-gray-600" />
            {t('manager.modules.newPickingLists.lockHistory.title', {}, 'Låsehistorikk (ta-og-slipp)')}
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={7}>{t('manager.modules.newPickingLists.lockHistory.last7Days', {}, 'Siste 7 dager')}</option>
              <option value={30}>{t('manager.modules.newPickingLists.lockHistory.last30Days', {}, 'Siste 30 dager')}</option>
              <option value={90}>{t('manager.modules.newPickingLists.lockHistory.last90Days', {}, 'Siste 90 dager')}</option>
            </select>
            <button
              onClick={() => loadData(days, operatorFilter)}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
            </button>
            <button onClick={onClose} className="p-2 rounded hover:bg-gray-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>
          )}

          {/* Per-operatør-oppsummering */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t('manager.modules.newPickingLists.lockHistory.summaryTitle', {}, 'Per operatør')}
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colLocks', {}, 'Tatt')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colUnlocks', {}, 'Sluppet')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colForceUnlocks', {}, 'Tvang')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colReassigns', {}, 'Omfordelt')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colAvgHeld', {}, 'Snitt holdetid')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('manager.modules.newPickingLists.lockHistory.colLastActivity', {}, 'Sist aktiv')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((row) => (
                    <tr
                      key={row.operatorID}
                      onClick={() => setOperatorFilter(operatorFilter === row.operatorID ? null : row.operatorID)}
                      className={`cursor-pointer hover:bg-blue-50 ${operatorFilter === row.operatorID ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{row.operatorID}</td>
                      <td className="px-3 py-2 text-right">{row.lockCount}</td>
                      <td className={`px-3 py-2 text-right ${row.unlockCount > 0 ? 'font-semibold text-orange-600' : ''}`}>{row.unlockCount}</td>
                      <td className="px-3 py-2 text-right">{row.forceUnlockCount}</td>
                      <td className="px-3 py-2 text-right">{row.reassignCount}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatHeldMinutes(row.avgHeldMinutes)}</td>
                      <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">{row.lastActivity ? formatTimestamp(row.lastActivity) : '-'}</td>
                    </tr>
                  ))}
                  {summary.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        {t('manager.modules.newPickingLists.lockHistory.noData', {}, 'Ingen lås/opplås-hendelser i perioden.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('manager.modules.newPickingLists.lockHistory.summaryHint', {}, 'Trykk på en rad for å filtrere hendelseslisten på operatøren.')}
            </p>
          </div>

          {/* Én rad per lås (TW 36396507) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t(`${lh}.periodsTitle`, {}, 'Låser')}
              {operatorFilter && (
                <span className="ml-2 text-xs font-normal text-blue-600">
                  ({operatorFilter} — {t(`${lh}.clearFilter`, {}, 'trykk raden igjen for å nullstille')})
                </span>
              )}
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colPList`, {}, 'Plukkliste')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colErpOrder`, {}, 'ERP-ordre')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colTaken`, {}, 'Tatt')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colReleased`, {}, 'Sluppet')}</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colHeld`, {}, 'Holdetid')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t(`${lh}.colStatus`, {}, 'Status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {periods.map((period) => {
                    const key = periodKey(period);
                    const detail = statusDetail(period);
                    const isHeldNow = period.status === 'HeldNow';
                    return (
                      <tr key={key} className={isHeldNow ? 'bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">#{period.pListID}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {(() => {
                            const erpOrders = (period.orderNosERP || '').split(',').filter(Boolean);
                            if (erpOrders.length === 0) return '-';
                            if (erpOrders.length === 1 || expandedErpKey === key) {
                              return <span className="whitespace-normal break-words">{erpOrders.join(', ')}</span>;
                            }
                            return (
                              <button
                                type="button"
                                onClick={() => setExpandedErpKey(key)}
                                className="text-left min-h-[44px] -my-3 inline-flex items-center gap-1.5 whitespace-nowrap"
                                aria-label={t(`${lh}.showAllErpOrders`, { '0': erpOrders.join(', ') }, `Vis alle ERP-ordre: ${erpOrders.join(', ')}`)}
                              >
                                {erpOrders[0]}
                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-xs font-semibold text-gray-600">+{erpOrders.length - 1}</span>
                              </button>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {period.heldBy || <span className="text-gray-500 italic">{t(`${lh}.noHolder`, {}, 'Ingen eier')}</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {period.takenAt ? (
                            <>
                              {formatTimestamp(period.takenAt)}
                              {period.takenBeforePeriod && (
                                <span className="block text-xs text-gray-500">{t(`${lh}.takenBeforePeriod`, {}, 'før perioden')}</span>
                              )}
                            </>
                          ) : (
                            <span className="italic text-amber-800">{t(`${lh}.takenUnknown`, {}, 'Ukjent')}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {period.releasedAt ? formatTimestamp(period.releasedAt) : '-'}
                        </td>
                        <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${isHeldNow ? 'font-semibold text-green-800' : ''}`}>
                          {formatHeldMinutes(heldMinutesFor(period))}
                        </td>
                        <td className="px-3 py-2">
                          {statusBadge(period)}
                          {detail && <span className="block text-xs text-gray-500 mt-0.5 whitespace-nowrap">{detail}</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {periods.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        {t(`${lh}.noData`, {}, 'Ingen lås/opplås-hendelser i perioden.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {hasUnlogged && (
              <p className="text-xs text-gray-500 mt-1">
                {t(`${lh}.legendUnlogged`, {}, '«Ukjent» og «Avsluttet uten logg»: låsen ble tatt eller sluppet på en måte som ikke er logget. Holdetiden kan da ikke beregnes.')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
